import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  Input,
  Option,
  Select,
  Sheet,
  Stack,
  Switch,
  Typography,
} from "@mui/joy";
import { useTheme } from "@mui/joy/styles";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import MDEditor from "@uiw/react-md-editor";
import ImageCropper from "../common/ImageCropper";
import EventPreview from "./EventPreview";
import { AdminEvent, uploadAdminMedia } from "../../api";
import { Event as EventItem } from "../../models/all";
import { getSevaGradientSurface } from "../../theme/branding";

export interface AdminEventFormValues {
  title: string;
  poster?: string;
  description?: string;
  purpose?: string;
  location?: string;
  startDate: string;
  endDate?: string;
  status?: "enabled" | "disabled";
  head?: string;
  hidden?: boolean;
  subPurposes: { name: string; amount?: number; strict: boolean }[];
}

interface Props {
  initial?: Partial<AdminEvent>;
  onSubmit: (data: AdminEventFormValues) => void | Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
}

const defaultValues: AdminEventFormValues = {
  title: "",
  poster: "",
  description: "",
  purpose: "",
  location: "",
  startDate: "",
  endDate: "",
  status: "enabled",
  head: "",
  hidden: false,
  subPurposes: [],
};

export default function AdminEventForm({ initial, onSubmit, onCancel, isSubmitting }: Props) {
  const theme = useTheme();
  const gradient = getSevaGradientSurface(theme, { withShadow: true });
  const [values, setValues] = useState<AdminEventFormValues>({
    ...defaultValues,
    ...initial,
    poster: initial?.poster ?? "",
    description: initial?.description ?? "",
    purpose: initial?.purpose ?? "",
    location: initial?.location ?? "",
    startDate: initial?.startDate ? initial.startDate.split("T")[0] : "",
    endDate: initial?.endDate ? initial.endDate.split("T")[0] : "",
    status: initial?.status ?? "enabled",
    head: initial?.head ?? "",
    hidden: initial?.hidden ?? false,
    subPurposes:
      initial?.subPurposes?.map((sp) => ({
        name: sp.name,
        amount: sp.amount ?? undefined,
        strict: sp.strict,
      })) || [],
  });

  const handleChange = (field: keyof AdminEventFormValues, value: any) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cropImage, setCropImage] = useState<string | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handlePosterSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCropImage(reader.result as string);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropDone = async (file: File) => {
    const url = await uploadAdminMedia(file, "events");
    setValues((prev) => ({ ...prev, poster: url }));
    setCropImage(null);
  };

  const handleSubPurposeChange = (
    index: number,
    field: "name" | "amount" | "strict",
    value: any,
  ) => {
    const updated = [...values.subPurposes];
    (updated[index] as any)[field] = value;
    setValues((prev) => ({ ...prev, subPurposes: updated }));
  };

  const addSubPurpose = () => {
    setValues((prev) => ({
      ...prev,
      subPurposes: [...prev.subPurposes, { name: "", amount: undefined, strict: false }],
    }));
  };

  const removeSubPurpose = (index: number) => {
    setValues((prev) => ({
      ...prev,
      subPurposes: prev.subPurposes.filter((_, i) => i !== index),
    }));
  };

  const previewEvent = useMemo<EventItem>(
    () => ({
      id: initial?.id ?? 0,
      title: values.title,
      poster: values.poster || "",
      description: values.description || "",
      purpose: values.purpose || "",
      location: values.location || "",
      startDate: values.startDate,
      endDate: values.endDate || "",
      status: values.status || "enabled",
      head: values.head || "",
      subPurposes: values.subPurposes.map((sp, i) => ({
        id: i,
        eventId: initial?.id ?? 0,
        name: sp.name,
        amount: sp.amount,
        strict: sp.strict,
      })),
    }),
    [values, initial],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(values);
  };

  return (
    <>
      <Stack component="form" spacing={3} onSubmit={handleSubmit} data-testid="admin-event-form">
        <Card
          variant="outlined"
          sx={{
            borderRadius: "xl",
            p: { xs: 2.5, md: 3 },
            gap: 3,
          }}
        >
          <Stack spacing={1}>
            <Typography level="title-md" sx={{ fontWeight: 700 }}>
              Event story
            </Typography>
            <Typography level="body-sm" sx={{ color: "text.secondary" }}>
              Share the heart behind this festival so devotees feel guided even before they arrive.
            </Typography>
          </Stack>
          <Grid container spacing={2}>
            <Grid xs={12} md={7}>
              <Stack spacing={2}>
                <FormControl required>
                  <FormLabel htmlFor="event-title">Title</FormLabel>
                  <Input
                    id="event-title"
                    value={values.title}
                    onChange={(e) => handleChange("title", e.target.value)}
                    placeholder="Janmashtami Seva Evening"
                    required
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Description (Markdown)</FormLabel>
                  <MDEditor
                    value={values.description}
                    onChange={(v) => handleChange("description", v || "")}
                    textareaProps={{ "aria-label": "Description (Markdown)" }}
                  />
                  <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
                    <Button
                      size="sm"
                      variant="outlined"
                      startDecorator={<VisibilityRoundedIcon />}
                      onClick={() => setPreviewOpen(true)}
                    >
                      Preview
                    </Button>
                    {values.description ? (
                      <Button
                        size="sm"
                        variant="plain"
                        color="neutral"
                        startDecorator={<RestartAltRoundedIcon />}
                        onClick={() => handleChange("description", "")}
                      >
                        Clear copy
                      </Button>
                    ) : null}
                  </Stack>
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="event-purpose">Purpose</FormLabel>
                  <Input
                    id="event-purpose"
                    value={values.purpose}
                    placeholder="Support prasadam for visiting students"
                    onChange={(e) => handleChange("purpose", e.target.value)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="event-location">Location</FormLabel>
                  <Input
                    id="event-location"
                    value={values.location}
                    placeholder="ISKCON Kanpur Temple Grounds"
                    onChange={(e) => handleChange("location", e.target.value)}
                  />
                </FormControl>
              </Stack>
            </Grid>
            <Grid xs={12} md={5}>
              <Sheet
                variant="solid"
                sx={{
                  background: gradient.background,
                  color: gradient.color,
                  boxShadow: gradient.boxShadow,
                  borderRadius: "lg",
                  p: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "radial-gradient(120% 70% at 10% 0%, rgba(255,255,255,0.25) 0%, transparent 70%), radial-gradient(120% 70% at 90% 100%, rgba(13,109,219,0.32) 0%, transparent 80%)",
                    pointerEvents: "none",
                  }}
                />
                <Stack spacing={1} sx={{ position: "relative", zIndex: 1 }}>
                  <Typography level="title-sm" sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}>
                    Event poster
                  </Typography>
                  <Typography level="body-sm" sx={{ color: gradient.muted }}>
                    Invite devotees visually. Upload a radiant flyer or seva photograph to headline the listing.
                  </Typography>
                </Stack>
                {values.poster ? (
                  <Box
                    sx={{
                      borderRadius: "md",
                      overflow: "hidden",
                      border: "1px solid rgba(255,255,255,0.32)",
                      boxShadow: gradient.boxShadow,
                    }}
                  >
                    <Box
                      component="img"
                      src={values.poster}
                      alt="Event poster"
                      sx={{ width: "100%", display: "block" }}
                    />
                  </Box>
                ) : null}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  ref={fileInputRef}
                  onChange={handlePosterSelect}
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ pt: 0.5 }}>
                  <Button
                    size="sm"
                    variant="soft"
                    color="neutral"
                    startDecorator={<UploadRoundedIcon />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {values.poster ? "Change poster" : "Upload poster"}
                  </Button>
                  {values.poster ? (
                    <Button
                      size="sm"
                      variant="plain"
                      color="neutral"
                      onClick={() => handleChange("poster", "")}
                    >
                      Remove
                    </Button>
                  ) : null}
                </Stack>
                <Chip size="sm" variant="soft" color="neutral" sx={{ alignSelf: "flex-start" }}>
                  JPG or PNG up to 2MB
                </Chip>
              </Sheet>
            </Grid>
          </Grid>
        </Card>

        <Card
          variant="outlined"
          sx={{ borderRadius: "xl", p: { xs: 2.5, md: 3 }, gap: 2.5 }}
        >
          <Stack spacing={1}>
            <Typography level="title-md" sx={{ fontWeight: 700 }}>
              Schedule & stewardship
            </Typography>
            <Typography level="body-sm" sx={{ color: "text.secondary" }}>
              Clarify when seva unfolds and who is guiding the celebration.
            </Typography>
          </Stack>
          <Grid container spacing={2}>
            <Grid xs={12} sm={6}>
              <FormControl required>
                <FormLabel htmlFor="event-start-date">Start Date</FormLabel>
                <Input
                  id="event-start-date"
                  type="date"
                  aria-label="Start Date"
                  data-testid="event-start-date"
                  value={values.startDate}
                  onChange={(e) => handleChange("startDate", e.target.value)}
                  required
                />
                <FormHelperText>When devotees should arrive or seva begins.</FormHelperText>
              </FormControl>
            </Grid>
            <Grid xs={12} sm={6}>
              <FormControl>
                <FormLabel htmlFor="event-end-date">End Date</FormLabel>
                <Input
                  id="event-end-date"
                  type="date"
                  aria-label="End Date"
                  data-testid="event-end-date"
                  value={values.endDate}
                  onChange={(e) => handleChange("endDate", e.target.value)}
                />
                <FormHelperText>Optional if this is a single-day celebration.</FormHelperText>
              </FormControl>
            </Grid>
            <Grid xs={12} sm={6}>
              <FormControl required>
                <FormLabel>Status</FormLabel>
                <Select
                  value={values.status}
                  onChange={(_, val) =>
                    handleChange("status", (val as AdminEventFormValues["status"]) ?? "enabled")
                  }
                  placeholder="Select status"
                >
                  <Option value="enabled">Published</Option>
                  <Option value="disabled">Hidden</Option>
                </Select>
                <FormHelperText>Control whether the event appears to devotees.</FormHelperText>
              </FormControl>
            </Grid>
            <Grid xs={12} sm={6}>
              <FormControl>
                <FormLabel>Managed by</FormLabel>
                <Select
                  value={values.head}
                  onChange={(_, val) => handleChange("head", val ?? "")}
                  placeholder="Assign a lead"
                >
                  <Option value="">Unassigned</Option>
                  <Option value="Bhisma">Bhisma</Option>
                  <Option value="Festival">Festival</Option>
                  <Option value="FOLK">FOLK</Option>
                  <Option value="Other">Other</Option>
                </Select>
                <FormHelperText>Choose who devotees can contact for seva guidance.</FormHelperText>
              </FormControl>
            </Grid>
            <Grid xs={12}>
              <Sheet
                variant="outlined"
                sx={{
                  borderRadius: "lg",
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Stack spacing={0.5}>
                  <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                    Hide from public listing
                  </Typography>
                  <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                    Keep preparations private while seva details are being finalized.
                  </Typography>
                </Stack>
                <Switch
                  checked={values.hidden}
                  onChange={(e) => handleChange("hidden", e.target.checked)}
                  sx={{ "--Switch-trackWidth": "44px" }}
                />
              </Sheet>
            </Grid>
          </Grid>
        </Card>

        <Card
          variant="outlined"
          sx={{ borderRadius: "xl", p: { xs: 2.5, md: 3 }, gap: 2 }}
        >
          <Stack spacing={1}>
            <Typography level="title-md" sx={{ fontWeight: 700 }}>
              Seva opportunities
            </Typography>
            <Typography level="body-sm" sx={{ color: "text.secondary" }}>
              List focused offerings so supporters know how to participate tangibly.
            </Typography>
          </Stack>
          <Stack spacing={1.5}>
            {values.subPurposes.length === 0 ? (
              <Sheet
                variant="soft"
                color="neutral"
                sx={{ borderRadius: "lg", p: 2, textAlign: "center" }}
              >
                <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                  No sub-purposes yet. Add sponsorships or seva focus areas below.
                </Typography>
              </Sheet>
            ) : (
              values.subPurposes.map((sp, idx) => (
                <Card key={idx} variant="outlined" sx={{ borderRadius: "lg", p: 2, gap: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                      Offering {idx + 1}
                    </Typography>
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="neutral"
                      aria-label={`remove-sub-purpose-${idx}`}
                      onClick={() => removeSubPurpose(idx)}
                    >
                      <DeleteForeverRoundedIcon />
                    </IconButton>
                  </Stack>
                  <Grid container spacing={1.5}>
                    <Grid xs={12} md={6}>
                      <FormControl>
                        <FormLabel>Name</FormLabel>
                        <Input
                          value={sp.name}
                          placeholder="Sponsor prasadam"
                          onChange={(e) => handleSubPurposeChange(idx, "name", e.target.value)}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} md={3}>
                      <FormControl>
                        <FormLabel>Suggested amount</FormLabel>
                        <Input
                          type="number"
                          value={sp.amount ?? ""}
                          placeholder="5000"
                          onChange={(e) =>
                            handleSubPurposeChange(
                              idx,
                              "amount",
                              e.target.value === "" ? undefined : Number(e.target.value),
                            )
                          }
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} md={3}>
                      <FormControl orientation="horizontal" sx={{ alignItems: "center", gap: 1 }}>
                        <FormLabel>Strict amount</FormLabel>
                        <Switch
                          checked={sp.strict}
                          onChange={(e) => handleSubPurposeChange(idx, "strict", e.target.checked)}
                          sx={{ "--Switch-trackWidth": "44px" }}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                </Card>
              ))
            )}
            <Button
              type="button"
              variant="soft"
              color="primary"
              startDecorator={<AddRoundedIcon />}
              onClick={addSubPurpose}
              sx={{ alignSelf: { xs: "stretch", sm: "flex-start" } }}
            >
              Add seva focus
            </Button>
          </Stack>
        </Card>

        <Divider />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="flex-end">
          {onCancel ? (
            <Button variant="plain" color="neutral" onClick={onCancel}>
              Cancel
            </Button>
          ) : null}
          <Button type="submit" variant="solid" color="primary" loading={isSubmitting}>
            {initial ? "Update event" : "Create event"}
          </Button>
        </Stack>
      </Stack>

      {cropImage ? (
        <ImageCropper
          image={cropImage}
          open={cropOpen}
          onClose={() => {
            setCropOpen(false);
            setCropImage(null);
          }}
          onCropDone={handleCropDone}
        />
      ) : null}
      <EventPreview open={previewOpen} onClose={() => setPreviewOpen(false)} event={previewEvent} />
    </>
  );
}
